import { useEffect, useRef, useState } from "react";

type Props = { otp: string };

type Stage = "loading-geo" | "ready" | "submitting" | "done" | "error" | "denied-geo";

export function Incident({ otp }: Props) {
  const [stage, setStage] = useState<Stage>("loading-geo");
  const [geo, setGeo] = useState<GeolocationPosition | null>(null);
  const [desc, setDesc] = useState("");
  const [photoName, setPhotoName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ref, setRef] = useState<string | null>(null);
  const photoRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!navigator.geolocation) {
      setStage("denied-geo");
      setError("Geolocation is not supported in this browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGeo(pos); setStage("ready"); },
      (err) => { setError(err.message || "Location access was denied."); setStage("denied-geo"); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  }, []);

  const submit = async () => {
    if (!geo) { setError("Location is required."); return; }
    if (desc.trim() === "") { setError("Description is required."); return; }
    setStage("submitting"); setError(null);
    const fd = new FormData();
    fd.set("description", desc);
    fd.set("lat", String(geo.coords.latitude));
    fd.set("lng", String(geo.coords.longitude));
    const file = photoRef.current?.files?.[0];
    if (file) fd.set("photo", file, file.name);
    try {
      const r = await fetch(`/c/${otp}/incident`, { method: "POST", body: fd });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error ?? `HTTP ${r.status}`);
      setRef(body.guardian_ref ?? "unknown");
      setStage("done");
    } catch (err: any) {
      setError(err?.message || "Submit failed.");
      setStage("ready");
    }
  };

  return (
    <main style={{ fontFamily: "system-ui", maxWidth: 560, margin: "2rem auto", padding: "0 1rem" }}>
      <h2>Report an incident</h2>
      {stage === "loading-geo" && <p>Locating…</p>}
      {stage === "denied-geo" && (
        <>
          <p style={{ color: "#b00" }}>Location is required. {error}</p>
          <p>Enable location access and reload.</p>
        </>
      )}
      {(stage === "ready" || stage === "submitting") && (
        <>
          <label htmlFor="desc">What happened?</label>
          <textarea
            id="desc" value={desc} onChange={(e) => setDesc(e.target.value)}
            rows={5} style={{ width: "100%", fontFamily: "inherit", padding: "0.5rem", fontSize: "1rem" }}
          />
          <p style={{ marginTop: "1rem" }}>
            <label>Photo (optional):</label><br />
            <input ref={photoRef} type="file" accept="image/*"
                   onChange={(e) => setPhotoName(e.target.files?.[0]?.name ?? null)} />
            {photoName && <span style={{ color: "#555", marginLeft: 8 }}>{photoName}</span>}
          </p>
          <small style={{ color: "#777" }}>GPS: ±{geo?.coords.accuracy.toFixed(0)}m</small>
          <br /><br />
          <button onClick={submit} disabled={stage === "submitting"} style={buttonStyle}>
            {stage === "submitting" ? "Submitting…" : "Submit incident"}
          </button>
          {error && <p style={{ color: "#b00" }}>{error}</p>}
        </>
      )}
      {stage === "done" && (
        <>
          <p style={{ color: "#2a7a2a" }}>Incident reported.</p>
          <p style={{ color: "#777" }}>Reference: {ref}</p>
        </>
      )}
    </main>
  );
}

const buttonStyle: React.CSSProperties = {
  padding: "0.9rem 1.2rem",
  fontSize: "1.05rem",
  background: "#1e40af",
  color: "white",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
};
